import {
    Close as CloseIcon,
    GetApp as InstallIcon,
    PhoneIphone as PhoneIcon,
    Share as ShareIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { pwaManager } from '../utils/pwa';

interface PWAInstallProps {
  showInstructions?: boolean;
}

const PWAInstall: React.FC<PWAInstallProps> = ({ showInstructions = false }) => {
  const [canInstall, setCanInstall] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(showInstructions);
  const [installInstructions, setInstallInstructions] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showUpdateMessage, setShowUpdateMessage] = useState(false);

  useEffect(() => {
    // Check if app can be installed
    setCanInstall(pwaManager.canInstall());

    // Listen for PWA events
    const handleInstallAvailable = () => {
      setCanInstall(true);
    };

    const handleInstallCompleted = () => {
      setCanInstall(false);
      setShowInstallDialog(false);
    };

    const handleInstallSuccess = (event: CustomEvent) => {
      setShowSuccessMessage(true);
    };

    const handleUpdateAvailable = (event: CustomEvent) => {
      setShowUpdateMessage(true);
    };

    const handleShowInstructions = (event: CustomEvent) => {
      const { instructions, isIOS: deviceIsIOS } = event.detail;
      setInstallInstructions(instructions);
      setIsIOS(deviceIsIOS);
      setShowInstructionsDialog(true);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-install-completed', handleInstallCompleted);
    window.addEventListener('pwa-install-success', handleInstallSuccess as EventListener);
    window.addEventListener('pwa-update-available', handleUpdateAvailable as EventListener);
    window.addEventListener('pwa-show-instructions', handleShowInstructions as EventListener);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-install-completed', handleInstallCompleted);
      window.removeEventListener('pwa-install-success', handleInstallSuccess as EventListener);
      window.removeEventListener('pwa-update-available', handleUpdateAvailable as EventListener);
      window.removeEventListener('pwa-show-instructions', handleShowInstructions as EventListener);
    };
  }, []);

  const handleInstallClick = async () => {
    const success = await pwaManager.showInstallPrompt();
    if (!success) {
      // If automatic install failed, show manual instructions
      setShowInstructionsDialog(true);
    }
  };

  const handleShowInstructions = () => {
    setShowInstructionsDialog(true);
  };

  const handleCloseInstructions = () => {
    setShowInstructionsDialog(false);
  };

  const handleRefreshForUpdate = () => {
    window.location.reload();
  };

  // Don't show anything if app is already installed and running standalone
  if (pwaManager.isRunningStandalone()) {
    return null;
  }

  return (
    <>
      {/* Install Button/Card */}
      {canInstall && (
        <Card sx={{ mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InstallIcon color="primary" />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" color="primary">
                  Install Family Archive
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add to your home screen for easier access and better upload experience
                </Typography>
              </Box>
            </Box>
          </CardContent>
          <CardActions>
            <Button
              variant="contained"
              startIcon={<InstallIcon />}
              onClick={handleInstallClick}
              size="small"
            >
              Install App
            </Button>
            <Button
              variant="text"
              onClick={handleShowInstructions}
              size="small"
            >
              Show Instructions
            </Button>
          </CardActions>
        </Card>
      )}

      {/* Installation Instructions Dialog */}
      <Dialog
        open={showInstructionsDialog}
        onClose={handleCloseInstructions}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isIOS ? <PhoneIcon /> : <InstallIcon />}
          Install Family Archive
          <IconButton
            onClick={handleCloseInstructions}
            sx={{ ml: 'auto' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isIOS ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                To install Family Archive on your iPhone:
              </Typography>
              <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" component="div">
                  1. Tap the <ShareIcon sx={{ fontSize: 16, mx: 0.5 }} /> <strong>Share button</strong> at the bottom of Safari
                  <br />
                  2. Scroll down and tap <strong>"Add to Home Screen"</strong>
                  <br />
                  3. Tap <strong>"Add"</strong> to install the app
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This only works in Safari browser. If you're using Chrome or another browser, 
                  please open this page in Safari first.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                To install Family Archive:
              </Typography>
              <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" component="div">
                  1. Look for an <InstallIcon sx={{ fontSize: 16, mx: 0.5 }} /> install icon in your browser's address bar, OR
                  <br />
                  2. Open your browser menu (⋮) and select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
                </Typography>
              </Box>
            </Box>
          )}
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Benefits of Installing:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>Faster access from your home screen</li>
            <li>Better camera integration for photo/video uploads</li>
            <li>Works offline for viewing previously uploaded content</li>
            <li>Background upload capabilities</li>
            <li>Native app-like experience</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstructions}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
          Family Archive installed successfully! You can now access it from your home screen.
        </Alert>
      </Snackbar>

      {/* Update Available Message */}
      <Snackbar
        open={showUpdateMessage}
        onClose={() => setShowUpdateMessage(false)}
      >
        <Alert
          onClose={() => setShowUpdateMessage(false)}
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleRefreshForUpdate}>
              Refresh
            </Button>
          }
        >
          A new version is available. Refresh to update.
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstall;
